import { useCallback, useEffect, useState } from "react";
import { faCheckCircle, faExclamationTriangle, faSync } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import useFetchWithMsal from "../../../useFetchWithMsal";
import { thor_api_urls } from "../../common/Constants";

export const FIELD_DISPLAY_NAMES = {
    hypothesisIf:          "Hypothesis (If…)",
    hypothesisThen:        "Hypothesis (Then…)",
    hypothesisBecause:     "Hypothesis (Because…)",
    longTermStrategicGoal: "Long-term Strategic Goal",
    backgroundResearch:    "Background Research",
    audienceDescription:   "Audience Description",
    targetedStartDate:     "Target Start Date",
    daysToPower:           "Days to Power",
    productManager:        "Product Manager",
    jiraTicket:            "Jira Ticket",
    specUrl:               "Spec URL",
    ownerEmail:            "Owner Email",
    isLongRunning:         "Long-running Experiment",
    memberTest:            "Member Test",
};

export const humanizeMessage = (msg) => {
    if (!msg) return msg;
    return Object.entries(FIELD_DISPLAY_NAMES).reduce(
        (acc, [camel, label]) =>
            acc.replace(new RegExp(`\\b${camel}\\b`, 'g'), label),
        msg
    );
};

const MESSAGE_TAB_PATTERNS = [
    { pattern: /metric/i,                                                          tab: 'metrics',         label: 'Metrics' },
    { pattern: /bucket/i,                                                          tab: 'buckets',         label: 'Buckets' },
    { pattern: /allocat/i,                                                         tab: 'buckets',         label: 'Buckets' },
    { pattern: /override/i,                                                        tab: 'overrides',       label: 'Overrides' },
    { pattern: /approval/i,                                                        tab: 'approvals',       label: 'Approvals' },
    { pattern: /instrumentation/i,                                                 tab: 'instrumentation', label: 'Instrumentation' },
    { pattern: /days.*power|power.*days|daysToPower/i,                             tab: 'config',          label: 'Configuration' },
    { pattern: /target.*start.*date|targetedStartDate|Target Start Date/i,         tab: 'config',          label: 'Configuration' },
    { pattern: /hypothesis/i,                                                      tab: 'hypothesis',      label: 'Hypothesis' },
    { pattern: /strategic.*goal|background.*research|longTerm|backgroundResearch/i, tab: 'hypothesis',    label: 'Hypothesis' },
    { pattern: /description|audience/i,                                            tab: 'audiences',       label: 'Audiences' },
    { pattern: /analyst.*engineer|engineer.*contact|both.*analyst|analyst.*contact|Analyst and Engineer/i, tab: 'stakeholders', label: 'Stakeholders' },
    { pattern: /owner|jira|spec|manager|product/i,                                 tab: 'overview',        label: 'Overview' },
];

export const getTabForMessage = (msg) => {
    const match = MESSAGE_TAB_PATTERNS.find(({ pattern }) => pattern.test(msg));
    return match ?? null;
};

const ValidationChip = ({ experimentKey, onNavigate }) => {
    const { execute } = useFetchWithMsal();
    const [result, setResult] = useState(null);
    const [loading, setLoading] = useState(true);
    const [open, setOpen] = useState(false);
    const [rev, setRev] = useState(0);

    const refresh = () => { setOpen(false); setRev(r => r + 1); };

    const handleKeyDown = useCallback((e) => {
        if (e.key === 'Escape') setOpen(false);
    }, []);

    useEffect(() => {
        if (open) document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [open, handleKeyDown]);

    useEffect(() => {
        setLoading(true);
        setResult(null);
        setOpen(false);
        execute('GET', thor_api_urls.validateExperiment(experimentKey))
            .then(data => setResult(data))
            .catch(() => setResult(null))
            .finally(() => setLoading(false));
    }, [execute, experimentKey, rev]);

    if (loading) return (
        <span className="tag is-light is-small" style={{ gap: '0.3rem' }}>
            <FontAwesomeIcon icon={faSync} spin style={{ fontSize: '0.65rem' }} />
            Validating…
        </span>
    );

    if (!result) return null;

    const issues = result.failedValidationMessages ?? [];

    if ((result.valid ?? result.isValid) || issues.length === 0) return (
        <span className="tag is-success is-light is-small" style={{ gap: '0.3rem', cursor: 'default' }}>
            <FontAwesomeIcon icon={faCheckCircle} />
            Valid
            <FontAwesomeIcon icon={faSync} className="ml-1 is-clickable"
                title="Re-validate"
                style={{ opacity: 0.5, cursor: 'pointer', fontSize: '0.6rem' }}
                onClick={refresh} />
        </span>
    );

    return (
        // Fix 4 — max-width 380px, clean Bulma card styling, scannable list
        <div className={`dropdown ${open ? 'is-active' : ''}`} style={{ display: 'inline-flex' }}>
            <div className="dropdown-trigger">
                <span className="tag is-warning is-small is-clickable"
                      style={{ gap: '0.3rem', cursor: 'pointer' }}
                      onClick={() => setOpen(o => !o)}>
                    <FontAwesomeIcon icon={faExclamationTriangle} />
                    {issues.length} issue{issues.length !== 1 ? 's' : ''}
                    <FontAwesomeIcon icon={faSync} className="ml-1"
                        title="Re-validate"
                        style={{ opacity: 0.5, fontSize: '0.6rem' }}
                        onClick={e => { e.stopPropagation(); refresh(); }} />
                </span>
            </div>
            <div className="dropdown-menu" style={{ minWidth: '280px', maxWidth: '380px' }}>
                <div className="dropdown-content" style={{ maxHeight: '320px', overflowY: 'auto', padding: '0.75rem' }}>
                    <p className="has-text-weight-semibold is-size-7 mb-2"
                       style={{ borderBottom: '1px solid #dbdbdb', paddingBottom: '0.4rem' }}>
                        Validation Issues
                    </p>
                    <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
                        {issues.map((msg, i) => {
                            const humanMsg = humanizeMessage(msg);
                            const dest = onNavigate ? getTabForMessage(msg) : null;
                            return (
                                <li key={i} className="is-size-7 mb-2"
                                    style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: '0.5rem' }}>
                                    <span className="has-text-danger" style={{ flexShrink: 1 }}>
                                        &bull; {humanMsg}
                                    </span>
                                    {dest && (
                                        <a
                                            className="is-size-7"
                                            style={{
                                                whiteSpace: 'nowrap',
                                                flexShrink: 0,
                                                color: '#3273dc',
                                                textDecoration: 'none',
                                                cursor: 'pointer',
                                            }}
                                            onMouseEnter={e => { e.currentTarget.style.textDecoration = 'underline'; }}
                                            onMouseLeave={e => { e.currentTarget.style.textDecoration = 'none'; }}
                                            onClick={(e) => { e.preventDefault(); setOpen(false); onNavigate(dest.tab); }}
                                            href="#"
                                        >
                                            &rarr; Fix in {dest.label}
                                        </a>
                                    )}
                                </li>
                            );
                        })}
                    </ul>
                </div>
            </div>
            {open && <div style={{ position: 'fixed', inset: 0, zIndex: 1 }} onClick={() => setOpen(false)} />}
        </div>
    );
};

export default ValidationChip;
